import { CheckboxField, TableInput } from "../../../common/FormFields";
import { EditableTable } from "../../../common/Tables";
import AddRowBtn from "../../../common/AppSpecificConpnents/AddRowBtn";
import InputWithPen from "../../../common/Inputs/InputWithPen";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import PartsPopup from "../../../common/AppSpecificConpnents/PartsTable/PartsPopup";
import { generateStepPreview } from "../../../util/stepUtils";
import { useState, useMemo, useRef } from "react";

/**
 * StepDetailsTable - Editable table for step action details
 * 
 * Handles its own add row logic and parts modal internally. The component will:
 * - Check for existing blank rows before adding new ones
 * - Automatically set editing index when adding rows
 * - Update parent state via onStepDetailsChange and onEditingIndexChange
 * - Manage parts modal state and selection internally
 * 
 * @param {Object} props
 * @param {Array} props.stepDetails - Array of step detail rows
 * @param {function} props.onStepDetailsChange - Callback when step details change
 * @param {number} props.editingIndex - Index of currently editing row
 * @param {function} props.onEditingIndexChange - Callback when editing index changes
 * @param {boolean} props.isHideStepDetailsHeading - Whether to hide step details in document
 * @param {function} props.onHideStepDetailsHeadingChange - Callback for checkbox change
 * @param {function} props.onAddRow - Optional callback notification when row is added
 * @param {function} props.onUnsavedChanges - Callback to mark unsaved changes
 * @param {function} props.onShowWarning - Callback to show warning snackbar
 * @param {number} props.editingBomIndex - Index of currently editing row in BOM table (to prevent add row when editing)
 */
const StepDetailsTable = ({
  stepDetails = [],
  onStepDetailsChange,
  editingIndex,
  onEditingIndexChange,
  isHideStepDetailsHeading = false,
  onHideStepDetailsHeadingChange,
  onAddRow,
  onUnsavedChanges,
  onShowWarning,
  editingBomIndex = null,
}) => {
  // Parts Modal state
  const [isPartsModalOpen, setPartsModalOpen] = useState(false);
  const [selectedPartIndex, setSelectedPartIndex] = useState(null);
  
  // Temporary row state - only added to main state when saved
  const [pendingRow, setPendingRow] = useState(null);
  
  // Store original row data when editing starts (for cancel functionality)
  // Using useRef to ensure we always have the latest original data when cancel is called
  const originalRowDataRef = useRef(null);

  // Handle part selection from parts modal
  const handleSelectPart = (selectedPart) => {
    if (selectedPartIndex !== null) {
      // If selecting part for pending row
      if (selectedPartIndex === stepDetails.length && pendingRow !== null) {
        setPendingRow({
          ...pendingRow,
          partNumber: selectedPart.part_number,
          partId: selectedPart.part_id,
          description: selectedPart.description || "",
          part_ref: selectedPart.part_id,
          unit_ref: selectedPart.unit_type_id_ref,
          unit: selectedPart.unit_type_id_ref,
        });
      } else {
        // Selecting part for existing row
        const updated = [...stepDetails];
        updated[selectedPartIndex] = {
          ...updated[selectedPartIndex],
          partNumber: selectedPart.part_number,
          partId: selectedPart.part_id,
          description: selectedPart.description || "",
          part_ref: selectedPart.part_id,
          unit_ref: selectedPart.unit_type_id_ref,
          unit: selectedPart.unit_type_id_ref,
        };
        onStepDetailsChange(updated);
        onUnsavedChanges();
      }
    }
    setPartsModalOpen(false);
    setSelectedPartIndex(null);
  };

  const handleEdit = (index) => {
    if (editingIndex !== null || editingBomIndex !== null) {
      onShowWarning(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }
    // If editing pending row, just set editing index
    if (index === stepDetails.length && pendingRow !== null) {
      onEditingIndexChange(index);
      return;
    }
    
    // Editing existing row - clean the table first
    const cleanedStepDetails = stepDetails.filter((row, i) => {
      return (
        i === index ||
        !(row.isNew && !row.action && !row.partNumber && !row.qty)
      );
    });

    // Store a deep copy of the original row data AFTER filtering but BEFORE any modifications
    if (cleanedStepDetails[index]) {
      originalRowDataRef.current = JSON.parse(JSON.stringify(cleanedStepDetails[index]));
    }

    if (cleanedStepDetails[index]?.isNew) {
      cleanedStepDetails[index].isNew = false;
    }

    onStepDetailsChange(cleanedStepDetails);
    onEditingIndexChange(index);
    onUnsavedChanges();
  };

  const handleSave = (index) => {
    // Check if we're saving a pending row
    const rowToSave = index === stepDetails.length && pendingRow !== null 
      ? pendingRow 
      : stepDetails[index];
    
    // Validation: Action field is required
    if (!rowToSave.action || rowToSave.action.trim() === "") {
      // Show error via snackbar
      if (onShowWarning) {
        onShowWarning("Please enter an Action before saving.", "error");
      } else if (window.confirm) {
        alert("Please enter an Action before saving.");
      }
      return;
    }

    // If saving a pending row, add it to the main state
    if (index === stepDetails.length && pendingRow !== null) {
      const newRow = {
        ...pendingRow,
        qty: pendingRow.qty || 0,
        isNew: false, // Mark as saved
      };
      
      // Clean up any blank rows from main state
      const cleanedStepDetails = stepDetails.filter((row) => {
        return !(row.isNew && !row.action && !row.partNumber && !row.qty);
      });
      
      // Add the new saved row
      onStepDetailsChange([...cleanedStepDetails, newRow]);
      setPendingRow(null);
    } else {
      // Saving an existing row
      const updatedStepDetails = [...stepDetails];
      if (!updatedStepDetails[index].qty) {
        updatedStepDetails[index].qty = 0;
      }
      updatedStepDetails[index].isNew = false; // Mark as saved

      // Clean up any other blank rows (but not the current one we're saving)
      const cleanedStepDetails = updatedStepDetails.filter((row, i) => {
        return (
          i === index ||
          !(row.isNew && !row.action && !row.partNumber && !row.qty)
        );
      });

      onStepDetailsChange(cleanedStepDetails);
    }
    
    onUnsavedChanges();
    onEditingIndexChange(null);
    originalRowDataRef.current = null;
  };

  const handleCancel = (index) => {
    // If canceling a pending row, just clear it
    if (index === stepDetails.length && pendingRow !== null) {
      setPendingRow(null);
      onEditingIndexChange(null);
      originalRowDataRef.current = null;
      return;
    }
    
    // Cancel existing row - restore original data
    if (stepDetails[index]?.isNew) {
      const updated = [...stepDetails];
      updated.splice(index, 1);
      onStepDetailsChange(updated);
      onEditingIndexChange(null);
      originalRowDataRef.current = null;
    } else {
      // Restore the original row data
      if (originalRowDataRef.current !== null && stepDetails[index] !== undefined) {
        const updated = [...stepDetails];
        updated[index] = { ...originalRowDataRef.current };
        onStepDetailsChange(updated);
      }
      onEditingIndexChange(null);
      originalRowDataRef.current = null;
    }
    onUnsavedChanges();
  };

  const handleDelete = (index) => {
    if (editingIndex !== null || editingBomIndex !== null) {
      onShowWarning(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }
    // Can't delete pending row (just cancel it)
    if (index === stepDetails.length && pendingRow !== null) {
      setPendingRow(null);
      onEditingIndexChange(null);
      return;
    }
    
    // Delete existing row
    const cleanedStepDetails = stepDetails.filter((row, i) => {
      return (
        i !== index &&
        !(row.isNew && !row.action && !row.partNumber && !row.qty)
      );
    });
    onStepDetailsChange(cleanedStepDetails);
    onUnsavedChanges();
  };

  const updateStepDetail = (index, field, value) => {
    // If editing pending row, update pending state
    if (index === stepDetails.length && pendingRow !== null) {
      setPendingRow({ ...pendingRow, [field]: value });
      return;
    }
    
    // Update existing row
    const updated = [...stepDetails];
    updated[index][field] = value;
    onStepDetailsChange(updated);
    onUnsavedChanges();
  };

  const clearPartNumber = (index) => {
    // If clearing part number from pending row
    if (index === stepDetails.length && pendingRow !== null) {
      setPendingRow({
        ...pendingRow,
        partNumber: "",
        partId: "",
        description: "",
      });
      return;
    }
    
    // Clear from existing row
    const updated = [...stepDetails];
    updated[index] = {
      ...updated[index],
      partNumber: "",
      partId: "",
      description: "",
    };
    onStepDetailsChange(updated);
    onUnsavedChanges();
  };

  const handleAddRow = () => {
    // Check if BOM table is editing a row
    if (editingBomIndex !== null) {
      if (onShowWarning) {
        onShowWarning(
          "Please save or cancel the BOM table row before adding a new Step Details row.",
          "error"
        );
      }
      return;
    }

    // Check if an existing Step Details row is being edited (not a pending row)
    if (editingIndex !== null && editingIndex !== stepDetails.length) {
      if (onShowWarning) {
        onShowWarning(
          "Please save or cancel the current row before adding or editing another one.",
          "error"
        );
      }
      return;
    }

    // If there's already a pending row, just focus on it
    if (pendingRow !== null) {
      if (onShowWarning) {
        onShowWarning("Please save or cancel the current row before adding or editing another one.", "error");
      }
      onEditingIndexChange(stepDetails.length); // Edit the pending row
      return;
    }

    // Check if there's already a blank row in the main state
    const hasBlankRow = stepDetails.some(
      (row) => row.isNew && !row.action && !row.partNumber && !row.qty
    );

    if (hasBlankRow) {
      if (onShowWarning) {
        onShowWarning("Please save or cancel the current row before adding or editing another one.", "error");
      }
      const blankRowIndex = stepDetails.findIndex(
        (row) => row.isNew && !row.action && !row.partNumber && !row.qty
      );
      onEditingIndexChange(blankRowIndex);
      return;
    }

    // Create a new pending row (not added to state yet)
    const newRow = {
      action: "",
      partNumber: "",
      qty: "",
      unit: "",
      suggestion: "",
      description: "",
      isNew: true,
    };
    setPendingRow(newRow);
    onEditingIndexChange(stepDetails.length); // Set editing index to the "new" row position
  };

  const renderCell = (row, field) => {
    if (field === "partNumber") {
      return <p>{row[field]}</p>;
    }
    if (field === "qty") {
      return row[field] === 0 ? "" : row[field];
    }
    return row[field];
  };

  const renderEditCell = (row, field, index) => {
    if (field === "action") {
      return (
        <TableInput
          type="text"
          value={row.action}
          onChange={(e) => updateStepDetail(index, "action", e.target.value)}
        />
      );
    }

    if (field === "partNumber") {
      return (
        <InputWithPen
          val={row.partNumber}
          onClickOnPencil={() => {
            setSelectedPartIndex(index);
            setPartsModalOpen(true);
            onUnsavedChanges();
          }}
          onClear={() => clearPartNumber(index)}
        />
      );
    }

    if (field === "qty") {
      return (
        <TableInput
          type="number"
          min="0"
          value={row.qty === 0 ? "" : row.qty}
          onChange={(e) => {
            const value = Math.max(0, parseFloat(e.target.value) || 0);
            updateStepDetail(index, "qty", value);
          }}
        />
      );
    }

    return row[field];
  };

  return (
    <>
      <div className="step-details-header mt-4 font-bold">
        <p className="step-details-title">Step Details Table</p>
        {/* <CheckboxField
          label="Hide Step Details from Document"
          checked={isHideStepDetailsHeading}
          onChange={(e) => {
            onHideStepDetailsHeadingChange(e.target.checked);
            onUnsavedChanges();
          }}
          labelStyle={{ fontSize: "15px", fontWeight: "normal", marginBottom: "-3px" }}
        /> */}
      </div>

      <EditableTable
        className="table"
        style={{ marginTop: "-10px" }}
        columns={[
          { label: "Action", field: "action", width: "40%", align: "left" },
          { label: "Item Code", field: "partNumber", width: "40%" },
          { label: "Qty", field: "qty", width: "10%" },
          { label: "Action", type: "actions", width: "10%" },
        ]}
        data={useMemo(() => {
          // Include pending row in data for display/editing
          return pendingRow !== null && editingIndex === stepDetails.length
            ? [...stepDetails, pendingRow]
            : stepDetails;
        }, [stepDetails, pendingRow, editingIndex])}
        editingIndex={editingIndex}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
        renderCell={renderCell}
        renderEditCell={renderEditCell}
      />

      <AddRowBtn onClick={handleAddRow} style={{ marginTop: "10px", marginLeft: "-15px" }} />

      {(stepDetails.length > 0 || pendingRow !== null) && (
        <>
          <h3 className="step-preview-heading mt-6 font-bold">Step Details Preview</h3>
          <RenderHtml
            content={generateStepPreview(stepDetails)}
            descriptionBox={true}
            whiteBg={true}
          />
        </>
      )}

      {/* Parts Modal */}
      {isPartsModalOpen && (
        <PartsPopup
          handleSelectPart={handleSelectPart}
          setPartsModalOpen={setPartsModalOpen}
        />
      )}
    </>
  );
};

export default StepDetailsTable;

